/**
 * CommandPalette — Cmd+K quick navigation (Supabase / Linear style).
 *
 * Opens an overlay with a search input. Type to filter pages,
 * arrow keys to navigate, Enter to go. Esc or click outside to close.
 */

import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface CommandItem {
  id: string
  label: string
  group: string
  /** Soit un path react-router (default behavior = navigate), soit ignoré
   *  si `onSelect` est fourni au CommandPalette. */
  path: string
  keywords?: string[]  // extra search terms
}

interface Props {
  open: boolean
  onClose: () => void
  items: CommandItem[]
  /** Override du comportement par défaut. Si fourni, le clic/Enter sur un
   *  item appelle `onSelect(item)` au lieu de `navigate(item.path)`.
   *  Utile pour les portails sans router (ClientPortal/PartnerPortal qui
   *  utilisent un state `page` interne plutôt que des routes). */
  onSelect?: (item: CommandItem) => void
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export type { CommandItem }

export default function CommandPalette({ open, onClose, items, onSelect }: Props) {
  const [query, setQuery] = useState('')
  const [activeIdx, setActiveIdx] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()

  // Reset on open
  useEffect(() => {
    if (open) {
      setQuery('')
      setActiveIdx(0)
      // Focus after render
      requestAnimationFrame(() => inputRef.current?.focus())
    }
  }, [open])

  // Global Escape listener (works even if input doesn't have focus)
  useEffect(() => {
    if (!open) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        e.stopPropagation()
        onClose()
      }
    }
    window.addEventListener('keydown', handler, true)
    return () => window.removeEventListener('keydown', handler, true)
  }, [open, onClose])

  // Filter items
  const filtered = query.trim()
    ? items.filter(item => {
        const q = query.toLowerCase()
        return (
          item.label.toLowerCase().includes(q) ||
          item.group.toLowerCase().includes(q) ||
          item.path.toLowerCase().includes(q) ||
          item.keywords?.some(k => k.toLowerCase().includes(q))
        )
      })
    : items

  // Clamp active index
  useEffect(() => {
    setActiveIdx(idx => Math.min(idx, Math.max(0, filtered.length - 1)))
  }, [filtered.length])

  // Scroll active item into view
  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-idx="${activeIdx}"]`)
    el?.scrollIntoView({ block: 'nearest' })
  }, [activeIdx])

  const go = useCallback((item: CommandItem) => {
    onClose()
    if (onSelect) onSelect(item)
    else navigate(item.path)
  }, [onClose, navigate, onSelect])

  // Keyboard
  const onKeyDown = useCallback((e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setActiveIdx(i => Math.min(i + 1, filtered.length - 1))
        break
      case 'ArrowUp':
        e.preventDefault()
        setActiveIdx(i => Math.max(i - 1, 0))
        break
      case 'Enter':
        e.preventDefault()
        if (filtered[activeIdx]) go(filtered[activeIdx])
        break
      case 'Escape':
        e.preventDefault()
        onClose()
        break
    }
  }, [filtered, activeIdx, go, onClose])

  if (!open) return null

  // Group filtered items
  const groups = new Map<string, CommandItem[]>()
  for (const item of filtered) {
    if (!groups.has(item.group)) groups.set(item.group, [])
    groups.get(item.group)!.push(item)
  }

  let globalIdx = 0

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[100] bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-[101] flex items-start justify-center pt-[15vh] px-4" onClick={onClose}>
        <div className="w-full max-w-[520px] bg-surface-card rounded-xl shadow-2xl border border-ink-ghost overflow-hidden" onClick={e => e.stopPropagation()}>
          {/* Search input */}
          <div className="flex items-center gap-3 px-4 py-3 border-b border-ink-ghost/30">
            <svg className="w-4 h-4 text-ink-muted flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
            </svg>
            <input
              ref={inputRef}
              value={query}
              onChange={e => { setQuery(e.target.value); setActiveIdx(0) }}
              onKeyDown={onKeyDown}
              placeholder="Rechercher une page..."
              className="flex-1 text-body text-ink placeholder:text-ink-muted outline-none bg-transparent"
            />
            <kbd className="hidden sm:inline-flex items-center px-1.5 py-0.5 rounded bg-surface-mid text-label font-medium text-ink-muted border border-ink-ghost">
              ESC
            </kbd>
          </div>

          {/* Results */}
          <div ref={listRef} className="max-h-[360px] overflow-y-auto py-2">
            {filtered.length === 0 ? (
              <div className="px-4 py-8 text-center">
                <p className="text-data text-ink-muted">Aucun resultat pour "{query}"</p>
              </div>
            ) : (
              Array.from(groups.entries()).map(([groupName, groupItems]) => (
                <div key={groupName}>
                  <div className="px-4 pt-2 pb-1">
                    <span className="text-label font-semibold text-ink-muted uppercase tracking-wider">{groupName}</span>
                  </div>
                  {groupItems.map(item => {
                    const idx = globalIdx++
                    const isActive = idx === activeIdx
                    return (
                      <button
                        key={item.id}
                        data-idx={idx}
                        onClick={() => go(item)}
                        onMouseEnter={() => setActiveIdx(idx)}
                        className={`w-full flex items-center gap-3 px-4 py-2.5 text-left cursor-pointer transition-colors ${
                          isActive ? 'bg-primary/5' : 'hover:bg-surface-low'
                        }`}
                      >
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                          isActive ? 'bg-primary/10 text-primary' : 'bg-surface-mid text-ink-muted'
                        }`}>
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                          </svg>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className={`text-data font-medium truncate ${isActive ? 'text-primary' : 'text-ink'}`}>
                            {item.label}
                          </div>
                          <div className="text-caption text-ink-muted truncate">{item.path}</div>
                        </div>
                        {isActive && (
                          <kbd className="hidden sm:inline-flex items-center px-1.5 py-0.5 rounded bg-surface-mid text-label font-medium text-ink-muted border border-ink-ghost">
                            ↵
                          </kbd>
                        )}
                      </button>
                    )
                  })}
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          <div className="px-4 py-2 border-t border-ink-ghost/30 flex items-center gap-4">
            <div className="flex items-center gap-1.5 text-label text-ink-muted">
              <kbd className="px-1 py-0.5 rounded bg-surface-mid border border-ink-ghost font-medium">↑↓</kbd>
              <span>naviguer</span>
            </div>
            <div className="flex items-center gap-1.5 text-label text-ink-muted">
              <kbd className="px-1 py-0.5 rounded bg-surface-mid border border-ink-ghost font-medium">↵</kbd>
              <span>ouvrir</span>
            </div>
            <div className="flex items-center gap-1.5 text-label text-ink-muted">
              <kbd className="px-1 py-0.5 rounded bg-surface-mid border border-ink-ghost font-medium">esc</kbd>
              <span>fermer</span>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
